import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { MANDATE_THRESHOLD_PASS } from '@/config/indexThresholds';
import type { MandateOverview, MandateSectionStatus } from '@/types/mandateOverview';

type MandateOverviewProps = {
  overview: MandateOverview;
  className?: string;
  /** When true, omit outer panel chrome (used inside Nuclius Mandate section). */
  embedded?: boolean;
};

function statusLabel(status: MandateSectionStatus): string {
  switch (status) {
    case 'match':
      return 'Match';
    case 'mismatch':
      return 'Mismatch';
    case 'partial':
      return 'Partial';
    case 'not_configured':
      return 'Not configured';
    case 'unevaluated':
      return 'Unevaluated';
    default:
      return status;
  }
}

function statusColor(status: MandateSectionStatus): string {
  switch (status) {
    case 'match':
      return 'text-index-cleared';
    case 'mismatch':
      return 'text-index-risk';
    case 'partial':
      return 'text-index-rewrite';
    default:
      return 'text-white/40';
  }
}

export default function MandateOverviewPanel({ overview, className, embedded = false }: MandateOverviewProps) {
  const [expanded, setExpanded] = useState(false);
  const pct = overview.match_pct;
  const evaluable = overview.sections.filter((s) =>
    ['match', 'mismatch', 'partial'].includes(s.status),
  );
  const matches = overview.sections.filter((s) => s.status === 'match');
  const mismatches = overview.sections.filter((s) => s.status === 'mismatch');

  return (
    <div className={cn(embedded ? 'space-y-4' : 'ni-panel p-5 md:p-6 space-y-4', className)}>
      {!embedded && (
      <div>
        <p className="ni-label mb-2">Mandate overview</p>
        <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/45">
          Development mandate score %
        </p>
      </div>
      )}

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="max-w-xl">
          <p className="text-sm font-sans text-white/70 leading-relaxed">
            {pct != null ? (
              <>
                Nuclius Index has concluded that this script matches{' '}
                <span className="text-white font-semibold tabular-nums">{pct}%</span> of your
                commission mandate
              </>
            ) : (
              'Nuclius Index could not compute a mandate match score — configure mandate criteria to enable fit assessment.'
            )}
          </p>
          <p className="mt-2 text-xs font-sans text-white/40 leading-relaxed">
            This score reflects fit against your specific mandate only. It does not affect the
            film&apos;s overall Engagement Score.
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-4xl md:text-5xl font-sans font-bold tabular-nums text-white">
            {pct != null ? `${pct}%` : '—'}
          </p>
          {pct != null && (
            <p
              className={cn(
                'text-[10px] font-mono uppercase tracking-wider mt-1',
                overview.passes_mandate ? 'text-index-cleared' : 'text-index-rewrite',
              )}
            >
              {overview.passes_mandate ? 'Mandate pass' : `Below ${MANDATE_THRESHOLD_PASS}% threshold`}
            </p>
          )}
        </div>
      </div>

      {evaluable.length > 0 && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="w-full flex items-center justify-between gap-3 py-3 px-4 border border-[#2a2a2a] rounded-sm hover:bg-[#1a1a1a] transition-colors text-left"
        >
          <span className="text-[10px] font-mono uppercase tracking-wider text-white/50">
            {matches.length} match{matches.length !== 1 ? 'es' : ''} · {mismatches.length} mismatch
            {mismatches.length !== 1 ? 'es' : ''}
          </span>
          <span className="text-white/40">
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </span>
        </button>
      )}

      {expanded && (
        <div className="space-y-4 border-t border-[#2a2a2a] pt-4">
          {matches.length > 0 && (
            <div>
              <p className="text-[10px] font-mono uppercase tracking-wider text-index-cleared mb-2">
                Matches
              </p>
              <ul className="space-y-2">
                {matches.map((s) => (
                  <li
                    key={s.key}
                    className="text-sm font-sans border-l-2 border-index-cleared/50 pl-3 py-1"
                  >
                    <span className="text-white font-medium">{s.title}</span>
                    {s.mandate_values.length > 0 && (
                      <span className="text-white/45"> · {s.mandate_values.join(', ')}</span>
                    )}
                    <p className="text-xs text-white/40 mt-0.5">{s.detail}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {mismatches.length > 0 && (
            <div>
              <p className="text-[10px] font-mono uppercase tracking-wider text-index-risk mb-2">
                Mismatches
              </p>
              <ul className="space-y-2">
                {mismatches.map((s) => (
                  <li
                    key={s.key}
                    className="text-sm font-sans border-l-2 border-index-risk/50 pl-3 py-1"
                  >
                    <span className="text-white font-medium">{s.title}</span>
                    {s.mandate_values.length > 0 && (
                      <span className="text-white/45"> · {s.mandate_values.join(', ')}</span>
                    )}
                    <p className="text-xs text-white/40 mt-0.5">{s.detail}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {overview.sections
            .filter((s) => !['match', 'mismatch'].includes(s.status))
            .map((s) => (
              <div key={s.key} className="text-xs font-sans text-white/35 flex gap-2">
                <span className={cn('shrink-0 font-mono uppercase', statusColor(s.status))}>
                  {statusLabel(s.status)}
                </span>
                <span>
                  {s.title}
                  {s.detail ? ` — ${s.detail}` : ''}
                </span>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
